import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ProcSavedReport } from '@erp/rpc';

/**
 * List the current user's saved query-builder reports, most recently updated
 * first. Each entry embeds the full report document, so "Open" can load a
 * report without a second request.
 */
export function useProcSavedReportsList() {
  const query = useQuery(orpc.procurement.savedReports.list.queryOptions({}));
  return {
    ...query,
    reports: query.data?.reports ?? [],
  } satisfies typeof query & { reports: ProcSavedReport[] };
}

function useInvalidateList() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: orpc.procurement.savedReports.list.key(),
    });
}

/** Create a new saved report. Invalidates the list on success. */
export function useCreateProcSavedReport() {
  const invalidate = useInvalidateList();
  return useMutation(
    orpc.procurement.savedReports.create.mutationOptions({
      onSuccess: () => {
        void invalidate();
      },
    }),
  );
}

/** Overwrite an existing saved report (rename and/or replace its document). */
export function useUpdateProcSavedReport() {
  const invalidate = useInvalidateList();
  return useMutation(
    orpc.procurement.savedReports.update.mutationOptions({
      onSuccess: () => {
        void invalidate();
      },
    }),
  );
}

/** Permanently delete a saved report. Invalidates the list on success. */
export function useDeleteProcSavedReport() {
  const invalidate = useInvalidateList();
  return useMutation(
    orpc.procurement.savedReports.delete.mutationOptions({
      onSuccess: () => {
        void invalidate();
      },
    }),
  );
}
